import styled, { DefaultTheme } from 'styled-components';

import media from 'apps/promo/theme/media';

export type DiskBadgeVariant = 'primary' | 'secondary' | 'tertiary' | 'letterIndex' | 'selectedPassive' | 'transparent' | 'hasBorderedImg';

export type DiskBadgeSize = 'x-small' | 'small' | 'fixed-small' | 'large';

interface SizeProps {
    diskSize?: DiskBadgeSize;
}

interface VariantProps {
    variant?: DiskBadgeVariant;
}

interface StateProps {
    isDisabled?: boolean;
    isInactive?: boolean;
}

interface DiskBadgeBaseProps extends SizeProps, VariantProps, StateProps {
    noBorder?: boolean;
}

export type DiskBadgeContainerProps = DiskBadgeBaseProps;

export interface ImgWrapProps extends SizeProps, VariantProps, StateProps {
    withoutRadius?: boolean;
}

export type FlagIconContainerProps = StateProps;

type DiskBadgeTheme = DefaultTheme & {
    purple_24_tr: string;
    purple_100: string;
    purple_8_tr: string;
    grey_8_lc: string;
    white: string;
    white_72_tr: string;
    purple_16_tr: string;
    grey_28_tr: string;
    black: string;
    purple_40_tr: string;
};

const getTheme = (props: { theme: DefaultTheme }): DiskBadgeTheme => props.theme as DiskBadgeTheme;
const shouldForwardDiskBadgeProp = (prop: string) => !['diskSize', 'variant', 'isDisabled', 'isInactive', 'noBorder'].includes(prop);
const shouldForwardImgWrapProp = (prop: string) => !['diskSize', 'variant', 'isDisabled', 'isInactive', 'withoutRadius'].includes(prop);
const shouldForwardFlagIconProp = (prop: string) => !['isDisabled', 'isInactive'].includes(prop);

const getSize = (props: SizeProps) => {
    switch (props.diskSize) {
        case 'x-small':
            return '24px';
        case 'small':
            return '40px';
        case 'fixed-small':
            return '40px';
        case 'large':
            return '80px';
        default:
            return '40px';
    }
};
const getDesktopSize = (props: SizeProps) => {
    switch (props.diskSize) {
        case 'x-small':
            return '24px';
        case 'small':
            return '56px';
        case 'fixed-small':
            return '40px';
        case 'large':
            return '112px';
        default:
            return '56px';
    }
};
const getImgSize = (props: SizeProps & VariantProps) => {
    switch (props.diskSize) {
        case 'x-small':
            return '24px';
        case 'small':
            return props.variant === 'hasBorderedImg' ? '38px' : '40px';
        case 'large':
            return props.variant === 'hasBorderedImg' ? '78px' : '80px';
        default:
            return props.variant === 'hasBorderedImg' ? '38px' : '40px';
    }
};
const getImgDesktopSize = (props: SizeProps & VariantProps) => {
    switch (props.diskSize) {
        case 'x-small':
            return '24px';
        case 'small':
            return props.variant === 'hasBorderedImg' ? '54px' : '56px';
        case 'large':
            return props.variant === 'hasBorderedImg' ? '110px' : '112px';
        default:
            return props.variant === 'hasBorderedImg' ? '54px' : '56px';
    }
};
const getBackground = (props: DiskBadgeBaseProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    switch (props.variant) {
        case 'primary':
            return props.isDisabled ? theme.purple_24_tr : theme.purple_100;
        case 'secondary':
            return props.isDisabled ? theme.white : theme.purple_8_tr;
        case 'tertiary':
            return props.isDisabled ? 'transparent' : theme.grey_8_lc;
        case 'letterIndex':
            return theme.white;
        case 'selectedPassive':
            return theme.white_72_tr;
        case 'transparent':
            return 'transparent';
        case 'hasBorderedImg':
            return theme.white;
        default:
            return theme.white;
    }
};
const getBorder = (props: DiskBadgeBaseProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    if (props.variant !== 'hasBorderedImg' && props.noBorder) {
        return 'transparent';
    } else {
        switch (props.variant) {
            case 'primary':
                return props.isDisabled ? 'transparent' : theme.purple_100;
            case 'secondary':
                return props.isDisabled ? theme.purple_16_tr : 'transparent';
            case 'tertiary':
                return props.isDisabled ? theme.grey_28_tr : theme.grey_8_lc;
            case 'letterIndex':
                return props.isDisabled ? theme.white : theme.black;
            case 'selectedPassive':
                return theme.purple_40_tr;
            case 'transparent':
                return 'transparent';
            case 'hasBorderedImg':
                return theme.grey_28_tr;
            default:
                return theme.white;
        }
    }
};

export const DiskBadgeContainer = styled.span.withConfig({ shouldForwardProp: shouldForwardDiskBadgeProp })<DiskBadgeContainerProps>`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 50%;
    width: ${(props) => getSize(props)};
    height: ${(props) => getSize(props)};
    background: ${(props) => getBackground(props)};
    border: ${(props) => (props.variant === 'letterIndex' ? '2px' : '1px')} solid ${(props) => getBorder(props)};
    position: relative;

    ${media.desktop1920} {
        width: ${(props) => getDesktopSize(props)};
        height: ${(props) => getDesktopSize(props)};
    }
`;

export const ImgWrap = styled.span.withConfig({ shouldForwardProp: shouldForwardImgWrapProp })<ImgWrapProps>`
    width: ${(props) => getImgSize(props)};
    height: ${(props) => getImgSize(props)};
    flex-shrink: 0;
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    opacity: ${(props) => (props.isDisabled ? '0.24' : props.isInactive ? '0.48' : null)};
    overflow: hidden;
    border-radius: ${(props) => (props.withoutRadius ? null : '50%')};

    ${media.desktop1920} {
        width: ${(props) => getImgDesktopSize(props)};
        height: ${(props) => getImgDesktopSize(props)};
    }

    img {
        width: 100%;
        max-width: 100%;
        border-radius: ${(props) => (props.withoutRadius ? null : '50%')};
    }
`;

export const FlagIconWrap = styled.span`
    position: absolute;
    right: -4px;
    bottom: -4px;
    border: 2px solid ${(props) => getTheme(props).white};
    border-radius: 50%;
    display: inline-flex;
    background: ${(props) => getTheme(props).white};
    z-index: 1;

    ${media.desktop1920} {
        right: -2px;
        bottom: -2px;
    }
`;
export const FlagIconContainer = styled.span.withConfig({ shouldForwardProp: shouldForwardFlagIconProp })<FlagIconContainerProps>`
    display: inline-flex;
    opacity: ${(props) => (props.isDisabled ? '0.24' : props.isInactive ? '0.48' : null)};
`;
export const SecondFlagIconWrap = styled.span`
    position: absolute;
    right: -14px;
    bottom: -4px;
    border: 2px solid ${(props) => getTheme(props).white};
    border-radius: 50%;
    display: inline-flex;
    background: ${(props) => getTheme(props).white};

    ${media.desktop1920} {
        right: -12px;
        bottom: -2px;
    }
`;
